import { usePrevious } from '@mantine/hooks'
import { produce } from 'immer'
import { atom, getDefaultStore, useAtom } from 'jotai'
import { get, pick } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { ValueOf } from 'type-fest'
import { dynamicConfigClient } from 'src/services/client'
import { useMe } from 'src/services/helpers/user'
import useNow from 'src/utils/hooks/useNow'
import { useGrowthEventDynamicConfig } from './common/growthEventConfig'

const store = getDefaultStore()

export const GrowthEventName = {
  AugustEvent2025: 'augustEvent2025',
  // ThirdAnniversaryEve: 'thirdAnniversaryEve',
  // ThirdAnniversary: 'thirdAnniversary',
  // ThirdAnniversaryRetweet: 'thirdAnniversaryRetweet',
  // MioMvRetweet: 'mioMvRetweet',
  Christmas2025: 'christmasEvent2025',
  // XmasDrama2025: 'xmasDrama2025',
  NewYearEvent2026: 'newYearEvent2026',
  Valentine2026: 'valentineEvent2026',
  AprilFools2026: 'aprilFoolsEvent2026',
  SpringPromotion2026: 'springPromotionEvent2026',
} as const
export type GrowthEventName = ValueOf<typeof GrowthEventName>

type GrowthEventInfo = {
  configKey: string
  enabled?: boolean
  enabledForAdmin?: boolean
  /** ISO timestamp (inclusive) */
  startDate?: string
  /** ISO timestamp (exclusive) */
  endDate?: string
  /** Whether to display the countdown timer */
  countdown?: {
    enabled?: boolean
    startDate?: string
  }
  /** Promotion data */
  promotions?: Array<Record<string, any>>
}

const eventsAtom = atom<
  Record<
    /** Event name (identifier) */
    GrowthEventName,
    GrowthEventInfo
  >
>({
  [GrowthEventName.AugustEvent2025]: {
    configKey: 'augustEvent2025',
  },
  // [GrowthEventName.ThirdAnniversaryEve]: {
  //   configKey: 'thirdAnniversaryEve',
  // },
  // [GrowthEventName.MioMvRetweet]: {
  //   configKey: 'mioMvRetweet',
  // },
  [GrowthEventName.Christmas2025]: {
    configKey: 'christmasEvent2025',
  },
  [GrowthEventName.NewYearEvent2026]: {
    configKey: 'newYearEvent2026',
  },
  [GrowthEventName.Valentine2026]: {
    configKey: 'valentineEvent2026',
  },
  [GrowthEventName.AprilFools2026]: {
    configKey: 'aprilFoolsEvent2026',
  },
  [GrowthEventName.SpringPromotion2026]: {
    configKey: 'springPromotionEvent2026',
  },
})

export async function prepareGrowthEventData(eventName: GrowthEventName) {
  const events = store.get(eventsAtom)

  const configKey = events[eventName].configKey
  if (!configKey) return

  const config = await dynamicConfigClient('constants').then(res =>
    get(res, [configKey]),
  )
  if (config) {
    store.set(
      eventsAtom,
      produce(events => {
        Object.assign(events[eventName], pick(config, ['startDate', 'endDate']))
      }),
    )
  }
}

export function isGrowthEventEnabled(eventName: GrowthEventName): boolean {
  return store.get(eventsAtom)[eventName].enabled ?? false
}

export function isGrowthEventStarted(eventName: GrowthEventName): boolean {
  const { startDate } = store.get(eventsAtom)[eventName]
  return !!startDate && new Date(startDate).getTime() <= Date.now()
}

export function isGrowthEventEnded(eventName: GrowthEventName): boolean {
  const { endDate } = store.get(eventsAtom)[eventName]
  return !!endDate && new Date(endDate).getTime() < Date.now()
}

export function isGrowthEventOngoing(eventName: GrowthEventName): boolean {
  return (
    isGrowthEventEnabled(eventName) &&
    isGrowthEventStarted(eventName) &&
    !isGrowthEventEnded(eventName)
  )
}

/**
 * Hook to use growth event state
 */
export function useGrowthEvent(eventName: GrowthEventName) {
  const [_events, setEvents] = useAtom(eventsAtom)
  // A workaround to the deferred update of atom values.
  // Atom updates always take effect on the next tick. Therefore, when using a
  // combined data of an atom value with external states, there will be a half-stale
  // data as the result of one execution cycle.
  // Here we use a ref to store the should-be-updated value, shadowing the actual
  // atom value until Jotai state catches up.
  const nextEventsRef = useRef<typeof _events>(undefined)
  if (
    nextEventsRef.current !== undefined &&
    nextEventsRef.current === _events
  ) {
    // Jotai state catches up here, so the ref has done its job.
    nextEventsRef.current = undefined
  }

  // NOTE: Previous(Before Christmas2025) event config are stored in 'constants' instead of 'growthEvents'.
  // They're all ended and disabled so we're not providing compatibility for them.
  // oxfmt-ignore
  const { data: dynConsts, isLoading: isLoadingGrowthEvent } = useGrowthEventDynamicConfig()
  const config = get(dynConsts, _events[eventName]?.configKey)
  const prevConfig = usePrevious(config)

  if (config !== prevConfig) {
    // Store the should-be-updated value
    nextEventsRef.current = produce(_events, events => {
      Object.assign(
        events[eventName],
        pick(config, [
          'enabled',
          'enabledForAdmin',
          'startDate',
          'endDate',
          'countdown',
        ]),
      )
    })
  }

  // Trigger Jotai state update outside of render
  // Fix setState during rendering warning
  useEffect(() => {
    if (nextEventsRef.current !== undefined) {
      setEvents(nextEventsRef.current)
    }
  }, [config])

  // Shadow the atom value as if it's already updated
  const events = nextEventsRef.current ?? _events

  const {
    enabled: _enabled,
    enabledForAdmin,
    startDate,
    endDate,
    countdown,
  } = events[eventName] ?? { endDate: '2000-01-01T00:00:00Z' }

  const { beingAdmin, loading: isLoadingMe } = useMe()
  const enabled = _enabled || (beingAdmin && enabledForAdmin)

  const { started, ended, countdownStarted } = useNow(
    useCallback(
      now => ({
        started: !!startDate && new Date(startDate).getTime() <= now,
        ended: !!endDate && new Date(endDate).getTime() < now,
        countdownStarted:
          countdown?.enabled &&
          countdown.startDate &&
          new Date(countdown.startDate).getTime() <= now,
      }),
      [startDate, endDate, countdown],
    ),
  )

  const isLoading = isLoadingMe || isLoadingGrowthEvent

  return useMemo(() => {
    return {
      enabled,
      startDate,
      endDate,
      started,
      ended,
      ongoing: enabled && started && !ended,
      countdown,
      countdownStarted,

      isLoading,
    }
  }, [
    enabled,
    startDate,
    endDate,
    started,
    ended,
    countdown,
    countdownStarted,
    isLoading,
  ])
}
